import IPPageNumber from "app/pages/InteractiveProposal/InteractiveProposalDownload/IPPageNuber";
import SummaryBGImg from "app/pages/InteractiveProposal/images/summaryBg.svg";
import { getSessionStore } from "app/utils/sessionStore";
import { formatToCompactNumber, getThemeColors } from "app/utils/utils";

const HexagonItem = ({ item, index }: { item: any; index?: number }) => {
  const number = item?.number?.toString();
  const suffixes = ["K", "M", "B", "T", "%"];
  const endsWithSuffix = suffixes.find((s) => number?.includes(s));

  return (
    <div
      key={index}
      className="absolute flex items-center"
      style={{ top: `${item.top}px`, left: `${item.left}px` }}
    >
      <div className="relative">
        <svg
          width="100"
          height="100"
          viewBox="0 0 485.688 485.688"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M364.269,453.155H121.416L0,242.844L121.416,32.533h242.853l121.419,210.312L364.269,453.155z"
            fill={item.color}
          />
        </svg>
        <div
          className="absolute inset-0 flex flex-col items-center justify-center text-white conthrax-font"
          style={{ fontSize: endsWithSuffix ? "13px" : "14px" }}
        >
          <div style={{ lineHeight: endsWithSuffix ? "0.8" : "1" }}>{number}</div>
        </div>
      </div>

      <div
        className="h-[2px]"
        style={{
          width: item.left < 296 ? "125px" : "35px",
          background: "linear-gradient(to right, #666666 0%, rgba(102,102,102,0) 100%)",
          marginLeft: "-1px",
        }}
      />
      <div className="rounded-full bg-[#666666]" style={{ width: "7px", height: "7px" }} />
      <div style={{ color: "#495057" }}>
        <div className="text-base font-medium whitespace-nowrap ml-2.5">{item.label}</div>
        {item.label2 && (
          <div className="text-base font-medium whitespace-nowrap ml-2.5">{item.label2}</div>
        )}
      </div>
    </div>
  );
};

interface IRCSummaryPageProps {
  summary: any;
  pageNumber: string | number;
}

const IRCSummaryPage = ({ summary, pageNumber }: IRCSummaryPageProps) => {
  const { branding } = getSessionStore("lookUpResponse");
  const { primaryColor, secondaryColor } = getThemeColors(branding);

  const financing = summary?.financing;
  const phases = summary?.phases;
  const selectedYear = summary?.selectedYear ?? 1;

  // Collect per-insured policy values for selected year
  const policies = summary?.policies ?? [];
  const selectedYearReviews = policies.flatMap((p: any) =>
    (p.PolicyReview ?? []).filter((r: any) => r.year === selectedYear),
  );
  const totalAccountValue = selectedYearReviews.reduce(
    (sum: number, r: any) => sum + (r.policyAccountValue ?? 0),
    0,
  );
  const totalDeathBenefit = selectedYearReviews.reduce(
    (sum: number, r: any) => sum + (r.policyDB ?? 0),
    0,
  );

  // Current plan review
  const currentReview = (summary?.planReviews ?? []).find((r: any) => r.planYear === selectedYear);
  const confirmedCount = currentReview?.confirmedBy ?? 0;
  const skippedCount = currentReview?.skippedBy ?? 0;
  const totalReviewers = currentReview?.total ?? 0;

  const items = [
    {
      number: policies.length,
      label: "Policies",
      left: 226,
      top: 80,
      color: primaryColor,
    },
    {
      number: `$${formatToCompactNumber(financing?.financingValue ?? 0)}`,
      label: "Total Financing",
      left: 315,
      top: 150,
      color: primaryColor,
    },
    {
      number: `${financing?.initialRate ?? 0}%`,
      label: "Initial Rate",
      left: 226,
      top: 220,
      color: secondaryColor,
    },
    {
      number: `${Math.round(phases?.funding?.progress ?? 0)}%`,
      label: "Funding Phase",
      left: 315,
      top: 290,
      color: primaryColor,
    },
    {
      number: `${Math.round(phases?.growth?.progress ?? 0)}%`,
      label: "Growth Phase",
      left: 226,
      top: 360,
      color: secondaryColor,
    },
    {
      number: `$${formatToCompactNumber(totalAccountValue)}`,
      label: "Account Value",
      label2: `YEAR ${selectedYear}`,
      left: 315,
      top: 430,
      color: secondaryColor,
    },
    {
      number: `$${formatToCompactNumber(totalDeathBenefit)}`,
      label: "Death Benefit",
      label2: `YEAR ${selectedYear}`,
      left: 226,
      top: 500,
      color: primaryColor,
    },
    {
      number: `${confirmedCount}/${totalReviewers}`,
      label: "Review",
      label2: "Confirmed",
      left: 315,
      top: 570,
      color: secondaryColor,
    },
    {
      number: `${skippedCount}/${totalReviewers}`,
      label: "Review",
      label2: "Skipped",
      left: 226,
      top: 640,
      color: primaryColor,
    },
  ];

  return (
    <div
      className="h-full"
      style={{ backgroundImage: `url(${SummaryBGImg})`, backgroundSize: "cover" }}
    >
      <div className="flex flex-col px-[1em] w-full max-w-[56em] mx-auto p-[2em] h-full">
        <div className="text-xl font-bold text-center interactive-proposal-text conthrax-font">
          Summary
        </div>
        <div style={{ marginLeft: "-60px", marginTop: "-70px" }}>
          <div className="relative">
            {/* Left bracket connecting financing & costs group */}
            <div className="absolute left-[105px] top-[200px]">
              <svg
                className="z-0 overflow-visible"
                viewBox="-50 60 190 320"
                height="300"
                width="215"
              >
                <path
                  d="M 180 60 L 0 60 Q -20 60 -20 80 L -20 195 M -20 225 L -20 340 Q -20 360 0 360 L 180 360"
                  stroke="black"
                  strokeWidth="1.6"
                  fill="none"
                />
              </svg>
              <div
                className="absolute conthrax-font"
                style={{
                  left: "-10px",
                  top: "142px",
                  color: "#A6A6A6",
                  fontSize: "13px",
                  transform: "translateY(-50%)",
                }}
              >
                Financing & Phases
              </div>
            </div>

            {/* Left bracket connecting performance group */}
            <div className="absolute left-[50px] top-[532px]">
              <svg
                className="z-0 overflow-visible"
                viewBox="-50 -20 150 249"
                height="220"
                width="250"
              >
                <path
                  d="M 85 0 L 0 0 Q -20 0 -20 30 L -20 60 M -20 100 L -20 130 Q -20 160 0 160 L 85 160"
                  stroke="black"
                  strokeWidth="1.6"
                  fill="none"
                />
              </svg>
              <div
                className="absolute conthrax-font"
                style={{
                  left: "50px",
                  top: "90px",
                  color: "#A6A6A6",
                  fontSize: "13px",
                  transform: "translateY(-50%)",
                }}
              >
                Performance
              </div>
            </div>

            {items.map((item, index) => (
              <HexagonItem key={index} item={item} index={index} />
            ))}
          </div>
        </div>
        <div className="mt-auto px-[4em]">
          <IPPageNumber number={pageNumber} />
        </div>
      </div>
    </div>
  );
};

export default IRCSummaryPage;
